import EventEmitter, {type GeneralEventTypes} from './event-emitter';
import {signal, type WritableSignal} from './reactive/arr-waveform-store';
type PlayerOptions = {
  media?: HTMLMediaElement;
  mediaControls?: boolean;
  autoplay?: boolean;
  playbackRate?: number;
};
class Player<T extends GeneralEventTypes> extends EventEmitter<T> {
  protected media: HTMLMediaElement;
  private isExternalMedia = false;
  private _isPlaying: WritableSignal<boolean>;
  private _currentTime: WritableSignal<number>;
  private _duration: WritableSignal<number>;
  private _volume: WritableSignal<number>;
  private _muted: WritableSignal<boolean>;
  private _playbackRate: WritableSignal<number>;
  private _seeking: WritableSignal<boolean>;
  private reactiveMediaEventCleanups: Array<() => void> = [];
  public get isPlayingSignal(): WritableSignal<boolean> {
    return this._isPlaying;
  }
  public get currentTimeSignal(): WritableSignal<number> {
    return this._currentTime;
  }
  public get durationSignal(): WritableSignal<number> {
    return this._duration;
  }
  public get volumeSignal(): WritableSignal<number> {
    return this._volume;
  }
  public get mutedSignal(): WritableSignal<boolean> {
    return this._muted;
  }
  public get playbackRateSignal(): WritableSignal<number> {
    return this._playbackRate;
  }
  public get seekingSignal(): WritableSignal<boolean> {
    return this._seeking;
  }
  constructor(options: PlayerOptions) {
    super();
    if (options.media) {
      this.media = options.media;
      this.isExternalMedia = true;
    } else {
      this.media = document.createElement('audio');
    }
    this._isPlaying = signal(false);
    this._currentTime = signal(0);
    this._duration = signal(this.media.duration || 0);
    this._volume = signal(this.media.volume);
    this._muted = signal(this.media.muted);
    this._playbackRate = signal(this.media.playbackRate || 1);
    this._seeking = signal(false);
    this.setupReactiveMediaEvents();
    if (options.mediaControls) {
      this.media.controls = true;
    }
    if (options.autoplay) {
      this.media.autoplay = true;
    }
    if (options.playbackRate != null) {
      this.onMediaEvent(
        'canplay',
        () => {
          if (options.playbackRate != null) {
            this.media.playbackRate = options.playbackRate;
          }
        },
        {once: true}
      );
    }
  }
  private setupReactiveMediaEvents() {
    this.reactiveMediaEventCleanups.push(
      this.onMediaEvent('play', () => {
        this._isPlaying.set(true);
      })
    );
    this.reactiveMediaEventCleanups.push(
      this.onMediaEvent('pause', () => {
        this._isPlaying.set(false);
      })
    );
    this.reactiveMediaEventCleanups.push(
      this.onMediaEvent('ended', () => {
        this._isPlaying.set(false);
      })
    );
    this.reactiveMediaEventCleanups.push(
      this.onMediaEvent('timeupdate', () => {
        this._currentTime.set(this.media.currentTime);
      })
    );
    this.reactiveMediaEventCleanups.push(
      this.onMediaEvent('durationchange', () => {
        this._duration.set(this.media.duration || 0);
      })
    );
    this.reactiveMediaEventCleanups.push(
      this.onMediaEvent('loadedmetadata', () => {
        this._duration.set(this.media.duration || 0);
      })
    );
    this.reactiveMediaEventCleanups.push(
      this.onMediaEvent('seeking', () => {
        this._seeking.set(true);
      })
    );
    this.reactiveMediaEventCleanups.push(
      this.onMediaEvent('seeked', () => {
        this._seeking.set(false);
      })
    );
    this.reactiveMediaEventCleanups.push(
      this.onMediaEvent('volumechange', () => {
        this._volume.set(this.media.volume);
        this._muted.set(this.media.muted);
      })
    );
    this.reactiveMediaEventCleanups.push(
      this.onMediaEvent('ratechange', () => {
        this._playbackRate.set(this.media.playbackRate);
      })
    );
  }
  protected onMediaEvent<K extends keyof HTMLElementEventMap>(
    event: K,
    callback: (ev: HTMLElementEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions
  ): () => void {
    this.media.addEventListener(event, callback, options);
    return () => this.media.removeEventListener(event, callback, options);
  }
  protected getSrc() {
    return this.media.currentSrc || this.media.src || '';
  }
  private revokeSrc() {
    const src = this.getSrc();
    if (src.startsWith('blob:')) {
      URL.revokeObjectURL(src);
    }
  }
  private canPlayType(type: string): boolean {
    return this.media.canPlayType(type) !== '';
  }
  protected setSrc(url: string, blob?: Blob) {
    const prevSrc = this.getSrc();
    if (url && prevSrc === url) return;
    this.revokeSrc();
    const newSrc =
      blob instanceof Blob && (this.canPlayType(blob.type) || !url)
        ? URL.createObjectURL(blob)
        : url;
    if (prevSrc) {
      this.media.removeAttribute('src');
    }
    if (newSrc || url) {
      try {
        this.media.src = newSrc;
      } catch {
        this.media.src = url;
      }
    }
  }
  protected destroy() {
    this.reactiveMediaEventCleanups.forEach((cleanup) => cleanup());
    this.reactiveMediaEventCleanups = [];
    if (this.isExternalMedia) return;
    this.media.pause();
    this.revokeSrc();
    this.media.removeAttribute('src');
    this.media.load();
    this.media.remove();
  }
  protected setMediaElement(element: HTMLMediaElement) {
    this.reactiveMediaEventCleanups.forEach((cleanup) => cleanup());
    this.reactiveMediaEventCleanups = [];
    this.media = element;
    this.setupReactiveMediaEvents();
  }
  public async play(): Promise<void> {
    try {
      return await this.media.play();
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      throw err;
    }
  }
  public pause(): void {
    this.media.pause();
  }
  public isPlaying(): boolean {
    return !this.media.paused && !this.media.ended;
  }
  public setTime(time: number) {
    this.media.currentTime = Math.max(0, Math.min(time, this.getDuration()));
  }
  public getDuration(): number {
    return this.media.duration;
  }
  public getCurrentTime(): number {
    return this.media.currentTime;
  }
  public getVolume(): number {
    return this.media.volume;
  }
  public setVolume(volume: number) {
    this.media.volume = volume;
  }
  public getMuted(): boolean {
    return this.media.muted;
  }
  public setMuted(muted: boolean) {
    this.media.muted = muted;
  }
  public getPlaybackRate(): number {
    return this.media.playbackRate;
  }
  public isSeeking(): boolean {
    return this.media.seeking;
  }
  public setPlaybackRate(rate: number, preservePitch?: boolean) {
    if (preservePitch != null) {
      this.media.preservesPitch = preservePitch;
    }
    this.media.playbackRate = rate;
  }
  public getMediaElement(): HTMLMediaElement {
    return this.media;
  }
  public setSinkId(sinkId: string): Promise<void> {
    const media = this.media as HTMLAudioElement & {
      setSinkId: (sinkId: string) => Promise<void>;
    };
    return media.setSinkId(sinkId);
  }
}
export default Player;
